import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Button,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import PaymentIcon from '@mui/icons-material/Payment';
import { useNavigate, useLocation } from 'react-router-dom';
import accountsApiService from '../../services/accountsApiService';
import { decryptText } from '../../utils/encryption';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/reactApi/api';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '₹ 0.00';
  return `₹ ${Number(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const OutstandingAmount = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);

  const urlusername = new URLSearchParams(location.search).get('username') || localStorage.getItem('username') || '';
  const username = decryptText(urlusername);
  const urltype = new URLSearchParams(location.search).get('type') || '';
  const pageType = urltype ? decryptText(urltype) : '';

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!username) {
          setError('User not logged in');
          setLoading(false);
          return;
        }
        const response = await accountsApiService.getOutstandingAmount(username);
        setData(response.value || []);
      } catch (err) {
        setError(err.message || 'Failed to fetch outstanding amounts');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [username]);

  const handleViewInvoice = async (billreference) => {
    try {
      setDownloadingInvoice(billreference);
      const url = `${API_BASE_URL}/accounts/business-central/invoice?invoiceNo=${encodeURIComponent(billreference)}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Invoice not available');
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `Invoice_${billreference.replace(/[^a-zA-Z0-9]/g, '-')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      alert('Failed to download invoice: ' + err.message);
    } finally {
      setDownloadingInvoice(null);
    }
  };

  const handleProceedPayment = async () => {
    try {
      setPaymentLoading(true);
      const orgId = localStorage.getItem('orgId');
      const appId = localStorage.getItem('appId');
      const labTypeId = localStorage.getItem('labTypeId') || '';

      const invoiceNumbers = unpaidData.map(r => r.Billreference).join(',');

      const result = await accountsApiService.initiateOutstandingPayment({
        orgId,
        appId,
        totalAmount: totalClosingBalance,
        invoiceNumbers,
        labTypeId,
      });

      if (result.success && result.paymentUrl) {
        window.location.href = result.paymentUrl;
      } else {
        alert('Failed to initiate payment. Please try again.');
      }
    } catch (err) {
      alert('Payment initiation failed: ' + (err.message || 'Unknown error'));
    } finally {
      setPaymentLoading(false);
    }
  };

  const unpaidData = data.filter(row => row.PaymentStatus !== 'Paid' && (row.BillOpeningBalance || 0) >= 0);
  const totalClosingBalance = unpaidData.reduce((sum, row) => sum + (row.BillClosingBalance || 0), 0);
  const netClosingBalance = data.reduce((sum, row) => sum + (row.BillClosingBalance || 0), 0);

  if (loading) {
    return (
      <Box sx={{ mt: 10, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'white', py: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          {pageType !== 'old' && (
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{ mr: 2, color: '#00024c' }}
            >
              Back
            </Button>
          )}
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#00024c' }}>
            Outstanding Amount
          </Typography>
        </Box>

        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : data.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No outstanding amounts found.</Typography>
          </Paper>
        ) : (
          <>
            <TableContainer component={Paper} elevation={2}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#00024c' }}>
                    {['S.No', 'Bill Reference', 'Bill Date', 'Party Name', 'Lab ID', 'Opening Balance', 'Closing Balance', 'Due Date', 'Overdue Days', 'Payment Status', 'Invoice'].map((header) => (
                      <TableCell key={header} sx={{ color: 'white', fontWeight: 600 }}>
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((row, index) => (
                    <TableRow key={row.Entry_No} sx={{ bgcolor: index % 2 === 0 ? '#f5f5f5' : 'white' }}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{row.Billreference}</TableCell>
                      <TableCell>{formatDate(row.BillDate)}</TableCell>
                      <TableCell>{row.BillPartyName}</TableCell>
                      <TableCell>{row.Labid}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{formatCurrency(row.BillOpeningBalance)}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{formatCurrency(row.BillClosingBalance)}</TableCell>
                      <TableCell>{formatDate(row.BillDueOn)}</TableCell>
                      <TableCell>
                        <Chip
                          label={`${row.BillOverDueDays} days`}
                          size="small"
                          color={row.BillOverDueDays > 30 ? 'error' : row.BillOverDueDays > 0 ? 'warning' : 'success'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.PaymentStatus || 'Not Paid'}
                          size="small"
                          color={row.PaymentStatus === 'Paid' ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          disabled={downloadingInvoice === row.Billreference}
                          onClick={() => handleViewInvoice(row.Billreference)}
                          sx={{ fontSize: '0.75rem' }}
                        >
                          {downloadingInvoice === row.Billreference ? 'Loading...' : 'Invoice'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                    <TableCell colSpan={6} sx={{ fontWeight: 700, textAlign: 'right', fontSize: '1rem' }}>
                      Net Total (Closing Balance):
                    </TableCell>
                    <TableCell colSpan={3}  sx={{ fontWeight: 700, fontSize: '1.1rem', color: netClosingBalance > 0 ? '#d32f2f' : '#2e7d32' }}>
                      {formatCurrency(netClosingBalance)}
                    </TableCell>
                    <TableCell colSpan={3} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {/* Proceed to Payment button — only show if there are unpaid bills and not old-type view */}
            {unpaidData.length > 0 && pageType !== 'old' && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<PaymentIcon />}
                  disabled={paymentLoading}
                  onClick={handleProceedPayment}
                  sx={{
                    bgcolor: '#E34124',
                    '&:hover': { bgcolor: '#c73518' },
                    px: 4,
                    py: 1.5,
                    fontWeight: 700,
                    fontSize: '1rem',
                  }}
                >
                  {paymentLoading ? 'Processing...' : `Proceed to Payment — ${formatCurrency(netClosingBalance)}`}
                </Button>
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default OutstandingAmount;
